import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { SITE } from "@/lib/site";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, ease: [0.22, 0.61, 0.36, 1], delay: 0.15 + i * 0.18 },
  }),
};

const Hero = () => {
  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative hero-vignette min-h-screen w-full flex items-center justify-center overflow-hidden"
    >
      {/* Subtle drifting wash */}
      <div
        aria-hidden="true"
        className="ambient-drift absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(40% 35% at 50% 30%, rgba(217,72,51,0.10), transparent 70%)",
        }}
      />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-10 pt-32 pb-24 text-center">
        {/* Cinder hairline + overline */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate="show"
          custom={0}
          className="flex flex-col items-center gap-5"
        >
          <span className="rule rule-cinder" aria-hidden="true" />
          <p data-testid={HERO.overline} className="overline">
            Book I &nbsp; · &nbsp; A Mythic Science-Fantasy Saga
          </p>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={fade}
          initial="hidden"
          animate="show"
          custom={1}
          data-testid={HERO.headline}
          className="font-display text-[#F3EFEA] mt-10 text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-[-0.02em]"
        >
          Shadows of the Infinite
          <span className="block mt-3 md:mt-4 italic font-light text-[#D9D4CD] text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem]">
            Echoes of Cinder
          </span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          variants={fade}
          initial="hidden"
          animate="show"
          custom={2}
          data-testid={HERO.subheadline}
          className="mt-10 max-w-2xl mx-auto text-[#A19D99] text-base sm:text-lg leading-relaxed font-light"
        >
          A mythic science-fantasy saga of war, memory, living machines, ancient
          relics, and cosmic erasure.
        </motion.p>

        {/* Pitch — italic editorial */}
        <motion.p
          variants={fade}
          initial="hidden"
          animate="show"
          custom={3}
          data-testid={HERO.pitch}
          className="mt-10 max-w-3xl mx-auto font-display italic text-[#F3EFEA] text-xl sm:text-2xl md:text-[1.6rem] leading-snug tracking-[-0.01em]"
        >
          A disgraced Ferrum pilot. A stolen Heart. A war against an enemy that
          does not conquer worlds — it makes the universe forget they ever
          existed.
        </motion.p>

        {/* CTAs */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate="show"
          custom={4}
          className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10"
        >
          <a
            data-testid={HERO.ctaPrimary}
            href={SITE.archiveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cinder"
          >
            Enter the Cinder Archive
            <ArrowRight size={16} strokeWidth={1.4} />
          </a>
          <a
            data-testid={HERO.ctaSecondary}
            href="#book-premise"
            className="btn-ghost"
          >
            Read the Premise
            <ArrowDown size={14} strokeWidth={1.4} />
          </a>
        </motion.div>

        {/* Bottom scroll cue */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate="show"
          custom={5}
          className="mt-24 flex justify-center"
          aria-hidden="true"
        >
          <span className="section-rule" />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
